import React, { useCallback, useEffect, useState } from "react";
import { Tabs, Card, Typography, Row, Col, Spin, Empty, Modal } from "antd";
import Api from "../../utils/Api"; // Adjust the import as necessary
import { Box } from "@mui/material";

const { TabPane } = Tabs;

function ListingsView() {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("All");
    const [categories, setCategories] = useState([]);
    const [selectedListing, setSelectedListing] = useState(null); // State to hold the selected listing
    const [isModalVisible, setIsModalVisible] = useState(false); // State to control modal visibility

    // Fetch categories from the API
    const fetchCategories = useCallback(async () => {
        try {
            const response = await Api.get("/categories");
            setCategories([{ id: 0, category_name: "All" }, ...response.data.data]); // Include "All" as the first category
        } catch (error) {
            console.error("Failed to fetch categories", error);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Fetch listings from the API
    useEffect(() => {
        const fetchListings = async () => {
            setLoading(true);
            try {
                const response = await Api.get('/listings'); // Ensure the API endpoint is correct
                setListings(response.data.data);
            } catch (error) {
                console.error("Failed to fetch listings", error);
            } finally {
                setLoading(false);
            }
        };

        fetchListings();
    }, []);

    // Filter listings by active category
    const filterListingsByCategory = (category) => {
        if (category === "All") {
            return listings;
        }

        // Split the selected category names by comma to handle multiple categories
        return listings.filter(listing => {
            const listingCategories = listing.category_names.split(',').map(cat => cat.trim());
            return listingCategories.includes(category); // Ensure we are checking for exact matches
        });
    };

    // Function to handle card click and open the modal
    const showModal = (listing) => {
        setSelectedListing(listing);
        setIsModalVisible(true);
    };

    // Function to handle modal close
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setSelectedListing(null); // Clear selected listing when closing modal
    };

    return (
        <div style={{ padding: "20px", backgroundColor: "#fff", color: "#000", minHeight: '100svh' }}>
            <Typography.Title level={2}>Listings</Typography.Title>

            <div style={{
                display: 'flex',
                justifyContent: 'center'
            }}>
                <Tabs
                    defaultActiveKey="All"
                    activeKey={activeCategory}
                    onChange={(key) => setActiveCategory(key)}
                    style={{ marginBottom: "20px" }}
                >
                    {categories.map((category) => (
                        <TabPane tab={category.category_name} key={category.category_name} />
                    ))}
                </Tabs>
            </div>

            {loading ? (
                <Spin tip="Loading..." />
            ) : (
                <Row gutter={16}>
                    {filterListingsByCategory(activeCategory).length > 0 ? (
                        filterListingsByCategory(activeCategory).map((listing) => (
                            <Col span={8} key={listing.id}>
                                <Card
                                    hoverable
                                    cover={
                                        <img
                                            alt={listing.listing_title}
                                            src={listing.listing_image}
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                aspectRatio: "16 / 9", // Set aspect ratio
                                                objectFit: "contain", // Maintain aspect ratio
                                            }}
                                        />
                                    }
                                    style={{
                                        marginBottom: "16px",
                                    }}
                                    onClick={() => showModal(listing)} // Open modal on card click
                                >
                                    <Card.Meta
                                        title={listing.listing_title}
                                        description={`${listing.companyName} - ${listing.category_names}`}
                                    />
                                </Card>
                            </Col>
                        ))
                    ) : (
                        <Col span={24}>
                            <Empty description="No listings available in this category." />
                        </Col>
                    )}
                </Row>
            )}

            {/* Modal to display selected listing details */}
            <Modal
                title={selectedListing ? selectedListing.listing_title : ""}
                visible={isModalVisible}
                onCancel={handleModalCancel}
                footer={null}
                style={{ top: 20 }}
            >
                {selectedListing && (
                    <div>
                        {/* Display the image at the top with company name overlay */}
                        <div style={{ position: "relative" }}>
                            <img
                                src={selectedListing.listing_image}
                                alt={selectedListing.listing_title}
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "6px",
                                    boxShadow: "0px 0px 10px rgba(0, 0, 0,  0.2)",
                                    marginBottom: "8px", // Add some space below the image
                                }}
                            />
                            <Typography
                                style={{
                                    position: "absolute",
                                    top: 8,
                                    right: 16,
                                    backgroundColor: "rgba(255, 255, 255, 0.8)", // White background with transparency
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    fontWeight: "bold",
                                    zIndex: 1,
                                }}
                            >
                                {selectedListing.companyName}
                            </Typography>
                        </div>

                        {/* Details below the image */}
                        <Box sx={{ padding: "16px", backgroundColor: "#f9f9f9", borderRadius: "6px", boxShadow: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                {selectedListing.listing_title}
                            </Typography>

                            <Typography sx={{
                                fontSize: 13,
                                margin: 0
                            }}>
                                {selectedListing.listing_description}
                            </Typography>

                            <Typography sx={{
                                fontSize: 16,
                                margin: 0
                            }}>
                                <b>Categories:</b> {selectedListing.category_names || 'No categories'}
                            </Typography>
                        </Box>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default ListingsView;